import { useCallback, useEffect, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import URLS from 'constants/urls';
import { reportError } from 'helpers/error-logging';
import { httpClient } from 'helpers/http';
import { selectActiveVenue } from 'selectors/general';

import { fetchPartnerOrders } from './SkuOrders.api';
import { DEFAULT_PARTNER_ORDERS_PAGE_SIZE, LIFECYCLE_STATUS, PARTNER_ORDERS_TAB } from './SkuOrders.constants';
import { skuOrdersKeys } from './skuOrdersKeys';

export function getPartnerOrdersPageRange(page, ordersCount, pageSize = DEFAULT_PARTNER_ORDERS_PAGE_SIZE) {
  const startNumber = ordersCount > 0 ? (page - 1) * pageSize + 1 : 0;
  const endNumber = ordersCount > 0 ? startNumber + ordersCount - 1 : 0;

  return { startNumber, endNumber };
}

export function usePartnerOrders(activeTab, options = {}) {
  const { lifecycleStatus = null, ...queryOptions } = options;
  const { venueId, timezone: venueTimezone, venueName } = useSelector(selectActiveVenue);
  const [page, setPage] = useState(1);
  const [cursorByPage, setCursorByPage] = useState({ 1: null });

  useEffect(() => {
    setPage(1);
    setCursorByPage({ 1: null });
  }, [venueId, activeTab, lifecycleStatus]);

  const cursor = cursorByPage[page] ?? null;

  const query = useQuery({
    queryKey: skuOrdersKeys.list(venueId, activeTab, cursor, lifecycleStatus),
    queryFn: () => fetchPartnerOrders({ venueId, tab: activeTab, venueTimezone, venueName, cursor, lifecycleStatus }),
    enabled: !!venueId,
    ...queryOptions,
  });

  const orders = query.data?.orders ?? [];
  const nextPageCursor = query.data?.nextPageCursor ?? null;

  const changePage = useCallback(
    nextPage => {
      if (nextPage > page && !nextPageCursor) return;

      if (nextPage > page) {
        setCursorByPage(previous => ({
          ...previous,
          [nextPage]: nextPageCursor,
        }));
      }
      setPage(nextPage);
    },
    [nextPageCursor, page]
  );

  const showPagination = page > 1 || !!nextPageCursor;
  const { startNumber, endNumber } = getPartnerOrdersPageRange(page, orders.length);

  return {
    ...query,
    orders,
    page,
    changePage,
    endNumber,
    nextPageCursor,
    showPagination,
    startNumber,
  };
}

export function useInvalidatePartnerOrders() {
  const queryClient = useQueryClient();
  return useCallback(() => queryClient.invalidateQueries({ queryKey: skuOrdersKeys.all }), [queryClient]);
}

// CancelOrder is async server-side; patch the react-query cache immediately after a 200 from cancelOrder.
export function applyOptimisticOrderCancelled(queryClient, { venueId, orderId, cancellationType }) {
  if (!venueId || !orderId || !cancellationType) return;

  const cancelledAt = new Date().toISOString();

  const updater = oldData => {
    if (!oldData?.orders?.length) return oldData;

    return {
      ...oldData,
      orders: oldData.orders.map(order => {
        if (order?.core?.orderId !== orderId) return order;

        return {
          ...order,
          lifecycleStatus: LIFECYCLE_STATUS.CANCELLED,
          orderItems: (order.orderItems || []).map(item => ({
            ...item,
            core: {
              ...item.core,
              cancelledAt,
              cancellationType,
            },
          })),
        };
      }),
    };
  };

  [PARTNER_ORDERS_TAB.TODAY, PARTNER_ORDERS_TAB.HISTORY].forEach(tab => {
    queryClient.setQueriesData({ queryKey: skuOrdersKeys.listPrefix(venueId, tab) }, updater);
  });
}

export function useApplyOptimisticOrderCancelled() {
  const queryClient = useQueryClient();
  const { venueId } = useSelector(selectActiveVenue);

  return useCallback(
    (orderId, cancellationType) => applyOptimisticOrderCancelled(queryClient, { venueId, orderId, cancellationType }),
    [queryClient, venueId]
  );
}

export function useFulfillSkuOrder(options = {}) {
  return useMutation({
    mutationFn: async orderId => {
      try {
        const url = URLS.skuOrders.fulfillOrder(orderId);
        const response = await httpClient.post(url, {}, { ignoreInternalError: true });
        return response?.data ?? null;
      } catch (error) {
        reportError(error);
        throw error;
      }
    },
    ...options,
  });
}

export function useCancelSkuOrder(options = {}) {
  return useMutation({
    mutationFn: async ({ orderId, cancellationType }) => {
      try {
        const url = URLS.skuOrders.cancelOrder(orderId);
        const response = await httpClient.post(
          url,
          { cancellation_type: cancellationType },
          { ignoreInternalError: true }
        );
        return response?.data ?? null;
      } catch (error) {
        reportError(error);
        throw error;
      }
    },
    ...options,
  });
}
